import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { memo, useCallback, useEffect, useMemo, useState, } from "react";
import { useTranslation } from "react-i18next";
import Button from "@ingka/button";
import { LoadingBall } from "@ingka/loading";
import Modal, { ModalHeader, Theatre } from "@ingka/modal";
import arrowCloudIn from "@ingka/ssr-icon/paths/arrow-cloud-in";
import Tabs, { Tab } from "@ingka/tabs";
import { useGetAdjustmentReasons, useGetForecastChanges, usePublishForecasts, useResetForecasts, } from "../../api/publishApi";
import { DeltaBadge } from "../../shared/components/DeltaBadge/DeltaBadge";
import { FORECAST_CHANGE_TYPE, PUBLISH_TAB } from "../../types/publish";
import { FISCAL_YEAR_END_DAY, FISCAL_YEAR_END_MONTH, getFiscalYearFullFromDate, } from "../../utils/forecastDateUtils";
import { filterDriversByDateRange, hasMatchingDateRange, transformDriverToTableRow, } from "../../utils/publishUtils";
import { DateRangePicker } from "../forecast/DatePicker";
import "./Publish.css";
import { PublishLoading } from "./PublishLoading";
import { PublishTable } from "./PublishTable";
import { ReasonSelector } from "./ReasonSelector";
const createSelectAllHandler = (setSelectedRows, drivers) => {
    return (checked) => {
        if (checked === true) {
            setSelectedRows(new Set(drivers.map((d) => d.driverId)));
        }
        else {
            setSelectedRows(new Set());
        }
    };
};
const createRowSelectHandler = (setSelectedRows, drivers) => {
    return (rowIndex, checked) => {
        const driverId = drivers[rowIndex]?.driverId;
        if (driverId === undefined)
            return;
        setSelectedRows((prev) => {
            const newSet = new Set(prev);
            if (checked === true) {
                newSet.add(driverId);
            }
            else {
                newSet.delete(driverId);
            }
            return newSet;
        });
    };
};
const PublishModal = memo(function PublishModal$({ onClose, unitId, divisionId, fetchApi }) {
    const { t } = useTranslation();
    const dateConstraints = useMemo(() => {
        const today = new Date();
        const currentFiscalYear = getFiscalYearFullFromDate(today);
        const nextFiscalYear = currentFiscalYear + 1;
        const minDate = format(today, "yyyy-MM-dd");
        const maxDate = format(new Date(nextFiscalYear, FISCAL_YEAR_END_MONTH, FISCAL_YEAR_END_DAY), "yyyy-MM-dd");
        return { minDate, maxDate };
    }, []);
    const initialDates = useMemo(() => {
        const today = new Date();
        const currentFiscalYear = getFiscalYearFullFromDate(today);
        return {
            from: format(today, "yyyy-MM-dd"),
            to: format(new Date(currentFiscalYear, FISCAL_YEAR_END_MONTH, FISCAL_YEAR_END_DAY), "yyyy-MM-dd"),
        };
    }, []);
    const [fromDate, setFromDate] = useState(initialDates.from);
    const [toDate, setToDate] = useState(initialDates.to);
    const [selectedReason, setSelectedReason] = useState();
    const [selectReasonPerChange, setSelectReasonPerChange] = useState(false);
    const [manualReasonValidationAttempted, setManualReasonValidationAttempted] = useState(false);
    const [activeTab, setActiveTab] = useState(PUBLISH_TAB.SYSTEM);
    const [manualReasons, setManualReasons] = useState({});
    const [selectedSystemRows, setSelectedSystemRows] = useState(new Set());
    const [selectedManualRows, setSelectedManualRows] = useState(new Set());
    const { data: adjustmentReasonsData, isLoading: isLoadingReasons } = useGetAdjustmentReasons(fetchApi, undefined);
    const adjustmentReasons = useMemo(() => {
        return adjustmentReasonsData?.payload ?? [];
    }, [adjustmentReasonsData]);
    const { data: systemChangesData, isLoading: isLoadingSystemChanges } = useGetForecastChanges(fetchApi, {
        unitId: unitId.toString(),
        divisionId: divisionId?.toString(),
        startDate: fromDate,
        endDate: toDate,
        changeType: FORECAST_CHANGE_TYPE.SYSTEM,
    }, undefined);
    const { data: manualChangesData, isLoading: isLoadingManualChanges } = useGetForecastChanges(fetchApi, {
        unitId: unitId.toString(),
        divisionId: divisionId?.toString(),
        startDate: fromDate,
        endDate: toDate,
        changeType: FORECAST_CHANGE_TYPE.MANUAL,
    }, undefined);
    const { mutate: publishForecastsMutate, isPending: isPublishing } = usePublishForecasts(fetchApi, {
        onSuccess: () => {
            setSelectedSystemRows(new Set());
            setSelectedManualRows(new Set());
            setManualReasons({});
            setSelectedReason(undefined);
            setManualReasonValidationAttempted(false);
        },
    });
    const { mutate: resetForecastsMutate, isPending: isResetting } = useResetForecasts(fetchApi, {
        onSuccess: () => {
            setSelectedManualRows(new Set());
            setManualReasons({});
            setSelectedReason(undefined);
            setManualReasonValidationAttempted(false);
        },
    });
    const filteredSystemData = useMemo(() => {
        const apiDrivers = systemChangesData?.payload?.drivers ?? [];
        const filtered = filterDriversByDateRange(apiDrivers, fromDate, toDate);
        return filtered.map(transformDriverToTableRow);
    }, [systemChangesData, fromDate, toDate]);
    const filteredManualData = useMemo(() => {
        const apiDrivers = manualChangesData?.payload?.drivers ?? [];
        const filtered = filterDriversByDateRange(apiDrivers, fromDate, toDate);
        return filtered.map(transformDriverToTableRow);
    }, [manualChangesData, fromDate, toDate]);
    const rawSystemDrivers = useMemo(() => {
        const apiDrivers = systemChangesData?.payload?.drivers ?? [];
        return filterDriversByDateRange(apiDrivers, fromDate, toDate);
    }, [systemChangesData, fromDate, toDate]);
    const rawManualDrivers = useMemo(() => {
        const apiDrivers = manualChangesData?.payload?.drivers ?? [];
        return filterDriversByDateRange(apiDrivers, fromDate, toDate);
    }, [manualChangesData, fromDate, toDate]);
    useEffect(() => {
        const hasSystemData = filteredSystemData.length > 0;
        const hasManualData = filteredManualData.length > 0;
        if (activeTab === PUBLISH_TAB.SYSTEM &&
            hasSystemData === false &&
            hasManualData === true) {
            setActiveTab(PUBLISH_TAB.MANUAL);
        }
        else if (activeTab === PUBLISH_TAB.MANUAL &&
            hasManualData === false &&
            hasSystemData === true) {
            setActiveTab(PUBLISH_TAB.SYSTEM);
        }
    }, [filteredSystemData.length, filteredManualData.length, activeTab]);
    const isPublishDisabled = useMemo(() => {
        if (isLoadingSystemChanges === true ||
            isLoadingManualChanges === true ||
            isLoadingReasons === true) {
            return true;
        }
        if (filteredSystemData.length === 0 && filteredManualData.length === 0) {
            return true;
        }
        return selectedSystemRows.size === 0 && selectedManualRows.size === 0;
    }, [
        isLoadingSystemChanges,
        isLoadingManualChanges,
        isLoadingReasons,
        filteredSystemData.length,
        filteredManualData.length,
        selectedSystemRows,
        selectedManualRows,
    ]);
    const manualReasonMissing = selectReasonPerChange === false &&
        selectedManualRows.size > 0 &&
        (selectedReason == null || selectedReason === "");
    const showManualReasonValidationError = manualReasonValidationAttempted && manualReasonMissing;
    const handleRowReasonSelect = useCallback((rowIndex, reasonId) => {
        const driverId = rawManualDrivers[rowIndex]?.driverId;
        if (driverId === undefined)
            return;
        setManualReasons((prev) => ({
            ...prev,
            [driverId]: reasonId,
        }));
    }, [rawManualDrivers]);
    const handleSystemSelectAll = useMemo(() => createSelectAllHandler(setSelectedSystemRows, rawSystemDrivers), [rawSystemDrivers]);
    const handleSystemRowSelect = useMemo(() => createRowSelectHandler(setSelectedSystemRows, rawSystemDrivers), [rawSystemDrivers]);
    const handleManualSelectAll = useMemo(() => createSelectAllHandler(setSelectedManualRows, rawManualDrivers), [rawManualDrivers]);
    const handleManualRowSelect = useMemo(() => createRowSelectHandler(setSelectedManualRows, rawManualDrivers), [rawManualDrivers]);
    useEffect(() => {
        if (selectReasonPerChange === true && selectedReason != null) {
            const newManualReasons = {};
            rawManualDrivers.forEach((driver) => {
                newManualReasons[driver.driverId] = selectedReason;
            });
            setManualReasons(newManualReasons);
        }
    }, [selectReasonPerChange, selectedReason, rawManualDrivers]);
    useEffect(() => {
        setSelectedSystemRows(new Set());
        setSelectedManualRows(new Set());
        setManualReasons({});
        setManualReasonValidationAttempted(false);
    }, [rawSystemDrivers, rawManualDrivers]);
    const activeTabId = activeTab === PUBLISH_TAB.MANUAL ? "tab_1" : "tab_0";
    const tabs = useMemo(() => {
        const getBadgeClass = (isActive) => `forecast-publish-modal__tab-badge forecast-publish-modal__tab-badge--${isActive ? "active" : "inactive"}`;
        return [
            _jsx(Tab, { id: "tab-btn-0", tabPanelId: "tab_0", text: _jsxs("span", { className: "forecast-publish-modal__tab-content", children: [t("publishModal.systemGenerated"), _jsx("span", { className: getBadgeClass(activeTab === PUBLISH_TAB.SYSTEM), children: _jsx(DeltaBadge, { variant: "neutral", label: String(filteredSystemData.length), size: "small" }) })] }) }, "0"),
            _jsx(Tab, { id: "tab-btn-1", tabPanelId: "tab_1", text: _jsxs("span", { className: "forecast-publish-modal__tab-content", children: [t("publishModal.manualAdjustments"), _jsx("span", { className: getBadgeClass(activeTab === PUBLISH_TAB.MANUAL), children: _jsx(DeltaBadge, { variant: "neutral", label: String(filteredManualData.length), size: "small" }) })] }) }, "1"),
        ];
    }, [activeTab, filteredSystemData.length, filteredManualData.length, t]);
    const handleTabChange = (tabId) => {
        setActiveTab(tabId === "tab_1" ? PUBLISH_TAB.MANUAL : PUBLISH_TAB.SYSTEM);
    };
    const handlePublish = useCallback(() => {
        const extractDriverAdjustments = (selectedDriverIds, reasons) => {
            return Array.from(selectedDriverIds).map((driverId) => {
                const reasonId = reasons?.[driverId];
                const adjustment = {
                    id: driverId,
                    ...(reasonId != null && reasonId !== "" ? { reasonId } : {}),
                };
                return adjustment;
            });
        };
        const getGlobalReason = () => {
            if (selectReasonPerChange === false) {
                return selectedReason;
            }
            return undefined;
        };
        const globalReason = getGlobalReason();
        if (selectedManualRows.size > 0 &&
            ((selectReasonPerChange === false &&
                (globalReason == null || globalReason === "")) ||
                (selectReasonPerChange === true &&
                    Array.from(selectedManualRows).some((driverId) => {
                        const rowReason = manualReasons[driverId];
                        return rowReason == null || rowReason === "";
                    })))) {
            setManualReasonValidationAttempted(true);
            return;
        }
        const manualDriverAdjustments = extractDriverAdjustments(selectedManualRows, selectReasonPerChange === true ? manualReasons : undefined).map((adj) => {
            if (globalReason !== undefined && globalReason !== "") {
                return { ...adj, reasonId: globalReason };
            }
            return adj;
        });
        const systemDriverAdjustments = extractDriverAdjustments(selectedSystemRows);
        if (manualDriverAdjustments.length === 0 &&
            systemDriverAdjustments.length === 0) {
            return;
        }
        const paramsList = [];
        if (manualDriverAdjustments.length > 0) {
            paramsList.push({
                driverAdjustments: manualDriverAdjustments,
                startDate: fromDate,
                endDate: toDate,
                source: "MANUAL",
            });
        }
        if (systemDriverAdjustments.length > 0) {
            paramsList.push({
                driverAdjustments: systemDriverAdjustments,
                startDate: fromDate,
                endDate: toDate,
                source: "SYSTEM",
            });
        }
        publishForecastsMutate(paramsList);
    }, [
        selectedManualRows,
        selectedSystemRows,
        manualReasons,
        selectReasonPerChange,
        selectedReason,
        fromDate,
        toDate,
        publishForecastsMutate,
    ]);
    const manualTableData = useMemo(() => filteredManualData.map((row) => ({
        ...row,
        reasonId: manualReasons[row.driverId],
    })), [filteredManualData, manualReasons]);
    const handleModalClose = isPublishing === true || isResetting === true ? undefined : onClose;
    const isActionInProgress = isPublishing === true || isResetting === true;
    const isResetDisabled = useMemo(() => {
        return (isActionInProgress ||
            isLoadingManualChanges === true ||
            selectedManualRows.size === 0);
    }, [isActionInProgress, isLoadingManualChanges, selectedManualRows.size]);
    const handleReset = useCallback(() => {
        if (selectedManualRows.size === 0)
            return;
        const driverAdjustments = Array.from(selectedManualRows).map((id) => ({
            id,
        }));
        resetForecastsMutate({
            driverAdjustments,
            startDate: fromDate,
            endDate: toDate,
        });
    }, [selectedManualRows, fromDate, toDate, resetForecastsMutate]);
    return (_jsx(Modal, { className: "forecast-publish-modal", visible: true, escapable: isActionInProgress === false, handleCloseBtn: handleModalClose, children: _jsx(Theatre, { header: _jsx(ModalHeader, { title: t("labels.publishForecast") }), children: _jsxs("div", { className: "forecast-publish-modal__container", children: [_jsxs("div", { className: "forecast-publish-modal__top-row", children: [_jsx(DateRangePicker, { fromDate: fromDate, toDate: toDate, minDate: dateConstraints.minDate, maxDate: dateConstraints.maxDate, onFromDateChange: setFromDate, onToDateChange: setToDate }), activeTab === PUBLISH_TAB.MANUAL && (_jsx(ReasonSelector, { reasons: adjustmentReasons, selectedReason: selectedReason, selectReasonPerChange: selectReasonPerChange, showValidationError: showManualReasonValidationError, isLoading: isLoadingReasons, onReasonChange: setSelectedReason, onToggleChange: setSelectReasonPerChange }))] }), _jsxs("div", { className: "forecast-publish-modal__tabs-section", children: [_jsx(Tabs, { activeTab: activeTabId, onTabChanged: handleTabChange, tabs: tabs }), activeTab === PUBLISH_TAB.SYSTEM ? (isPublishing === true ? (_jsx("div", { className: "forecast-publish-modal__loading", children: _jsx(PublishLoading, { text: t("publishModal.publishingInProgress") }) })) : isLoadingSystemChanges === true ? (_jsx("div", { className: "forecast-publish-modal__loading", children: _jsx(LoadingBall, {}) })) : (_jsx(PublishTable, { data: filteredSystemData, selectedRows: selectedSystemRows, showReasons: false, showValidationError: false, reasons: adjustmentReasons, onSelectAll: handleSystemSelectAll, onRowSelect: handleSystemRowSelect, onRowReasonChange: handleRowReasonSelect }))) : isPublishing === true || isResetting === true ? (_jsx("div", { className: "forecast-publish-modal__loading", children: _jsx(PublishLoading, { text: isResetting === true
                                        ? t("publishModal.resetting")
                                        : t("publishModal.publishingInProgress") }) })) : isLoadingManualChanges === true ? (_jsx("div", { className: "forecast-publish-modal__loading", children: _jsx(LoadingBall, {}) })) : (_jsx(PublishTable, { data: manualTableData, selectedRows: selectedManualRows, showReasons: selectReasonPerChange, showValidationError: manualReasonValidationAttempted, reasons: adjustmentReasons, onSelectAll: handleManualSelectAll, onRowSelect: handleManualRowSelect, onRowReasonChange: handleRowReasonSelect }))] }), _jsxs("div", { className: "forecast-publish-modal__footer", children: [_jsx("div", { className: "forecast-publish-modal__footer-left", children: activeTab === PUBLISH_TAB.MANUAL && (_jsx(Button, { type: "secondary", size: "small", text: isResetting === true
                                        ? t("publishModal.resetting")
                                        : t("publishModal.resetDraft"), onClick: handleReset, disabled: isResetDisabled === true })) }), _jsxs("div", { className: "forecast-publish-modal__footer-right", children: [_jsx(Button, { type: "secondary", size: "small", text: t("labels.cancel"), onClick: onClose, disabled: isActionInProgress === true }), _jsx(Button, { type: "primary", size: "small", text: isPublishing === true
                                            ? t("publishModal.publishing")
                                            : t("labels.publish"), onClick: handlePublish, disabled: isPublishDisabled === true || isActionInProgress === true })] })] })] }) }) }));
});
export const Publish = memo(function Publish$({ unitId, divisionId, fetchApi }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isModalOpen, setModalOpen] = useState(false);
    const dateRange = useMemo(() => {
        const today = new Date();
        const currentFiscalYear = getFiscalYearFullFromDate(today);
        return {
            from: format(today, "yyyy-MM-dd"),
            to: format(new Date(currentFiscalYear, FISCAL_YEAR_END_MONTH, FISCAL_YEAR_END_DAY), "yyyy-MM-dd"),
        };
    }, []);
    const { data: systemData, isLoading: isLoadingSystem } = useGetForecastChanges(fetchApi, {
        unitId: unitId.toString(),
        divisionId: divisionId?.toString(),
        startDate: dateRange.from,
        endDate: dateRange.to,
        changeType: FORECAST_CHANGE_TYPE.SYSTEM,
    }, undefined);
    const { data: manualData, isLoading: isLoadingManual } = useGetForecastChanges(fetchApi, {
        unitId: unitId.toString(),
        divisionId: divisionId?.toString(),
        startDate: dateRange.from,
        endDate: dateRange.to,
        changeType: FORECAST_CHANGE_TYPE.MANUAL,
    }, undefined);
    const hasSystemData = filterDriversByDateRange(systemData?.payload?.drivers ?? [], dateRange.from, dateRange.to).length > 0;
    const hasManualData = filterDriversByDateRange(manualData?.payload?.drivers ?? [], dateRange.from, dateRange.to).length > 0;
    const hasAnyData = hasSystemData || hasManualData;
    const isCheckingData = isLoadingSystem === true || isLoadingManual === true;
    const onClick = () => {
        queryClient.removeQueries({
            queryKey: ["forecast-changes"],
            predicate: (query) => !hasMatchingDateRange(query.queryKey, dateRange.from, dateRange.to),
        });
        queryClient.refetchQueries({
            queryKey: ["forecast-changes"],
        });
        setModalOpen(true);
    };
    const onClose = () => {
        setModalOpen(false);
    };
    return (_jsxs("div", { className: "forecast-publish-wrapper", children: [_jsx(Button, { type: "primary", size: "small", text: t("labels.publish"), onClick: onClick, ssrIcon: arrowCloudIn, iconPosition: "leading", disabled: isCheckingData === true || hasAnyData === false }), isModalOpen === true && (_jsx(PublishModal, { onClose: onClose, unitId: unitId, divisionId: divisionId, fetchApi: fetchApi }))] }));
});
//# sourceMappingURL=PublishModal.js.map